import {
  ColumnStats,
  ChartData,
  DateGap,
  DistributionCandidate,
  Fact,
  SummaryContent,
  TableBasicStats
} from './data-profile-response.model';

export interface ProfileStepResultBase<TStepId extends string, TData> {
  step_id: TStepId;
  success: boolean;
  data: TData;
  error: string | null;
  execution_time_ms: number;
  timestamp: string;
}

export interface ColumnBasicStatsStepData {
  columns: Record<string, ColumnStats>;
}

export type ColumnBasicStatsStepResult = ProfileStepResultBase<
  'column_basic_stats',
  ColumnBasicStatsStepData
>;

export interface TableBasicStatsStepData {
  table_basic: TableBasicStats;
}

export type TableBasicStatsStepResult = ProfileStepResultBase<
  'table_basic_stats',
  TableBasicStatsStepData
>;

export interface ColumnAdvancedStats {
  column_name: string;
  std_dev?: number;
  std_dev_unit?: string;
  q25?: number | string;
  q75?: number | string;
  iqr?: number;
  iqr_unit?: string;
  skewness?: number;
  skewness_interpretation?: string;
  kurtosis?: number;
  kurtosis_interpretation?: string;
  distribution_type?: string;
  distribution_confidence?: number;
  distribution_tests?: Record<string, number>;
  distribution_candidates?: DistributionCandidate[];
  distribution_test_used?: string;
  distribution_note?: string;
  pk_continuity?: number;
  completeness?: number;
  uniqueness?: number;
  consistency_score?: number;
  overall_quality?: number;
  freshness_score?: number;
  continuity_score?: number;
}

export interface ColumnAdvancedStatsStepData {
  columns: Record<string, ColumnAdvancedStats>;
}

export type ColumnAdvancedStatsStepResult = ProfileStepResultBase<
  'column_advanced_stats',
  ColumnAdvancedStatsStepData
>;

export interface TableAdvancedStatsStepData {
  table_advanced: {
    overall_quality: number | null;
    completeness: number | null;
    uniqueness: number | null;
    continuity: number | null;
    continuity_date: string | null;
    date_gaps: DateGap[];
    correlation_summary: Record<string, Record<string, number>>;
    significant_correlations?: unknown[];
    regression_summary: Record<string, unknown>;
    reg_predictive_quality?: number | null;
    forecast_summary: Record<string, unknown>;
    ts_predictive_quality: number | Record<string, unknown> | null;
  };
}

export type TableAdvancedStatsStepResult = ProfileStepResultBase<
  'table_advanced_stats',
  TableAdvancedStatsStepData
>;

export type ChartDataStepData = ChartData;

export type ChartDataStepResult = ProfileStepResultBase<
  'chart_data',
  ChartDataStepData
>;

export interface SummaryDataStepData {
  summary: SummaryContent;
}

export type SummaryDataStepResult = ProfileStepResultBase<
  'summary_data',
  SummaryDataStepData
>;

export type TopFactsStepData = [number, Fact][];

export type TopFactsStepResult = ProfileStepResultBase<
  'top_facts',
  TopFactsStepData
>;

export interface MissingAnalysisBin {
  start_idx: number;
  end_idx: number;
  mid_idx: number;
  missing_count: number;
  missing_ratio: number;
  first_missing_idx: number | null;
}

export interface ColumnMissingAnalysis {
  missing_count: number;
  missing_ratio: number;
  has_missing: boolean;
  bins: MissingAnalysisBin[];
}

export interface MissingAnalysisStepData {
  columns: Record<string, ColumnMissingAnalysis>;
  n_bins: number;
  n_rows: number;
}

export type MissingAnalysisStepResult = ProfileStepResultBase<
  'missing_analysis',
  MissingAnalysisStepData
>;

export interface FindingsStepFinding {
  code: string;
  severity: 'info' | 'warning' | 'medium' | 'error' | 'critical';
  reason: string;
  affected_count: number;
  affected_indices: number[];
  score: number;
  recommendations: string[] | null;
}

export interface FindingsStepColumnData {
  column_type: string;
  findings: FindingsStepFinding[];
  issue_count: number;
  max_severity: string;
}

export interface FindingsStepData {
  [columnName: string]: FindingsStepColumnData;
}

export type FindingsStepResult = ProfileStepResultBase<
  'findings',
  FindingsStepData
>;

export interface TableIntentStepData {
  category: string;
  confidence: number;
  subtopics_conf: number;
  top_subtopics: string;
}

export type TableIntentStepResult = ProfileStepResultBase<
  'table_intent',
  TableIntentStepData
>;

export interface FullAssessmentDimensionScore {
  confidence: number;
  issue_count: number;
  metric: string;
  score: number;
}

export interface FullAssessmentIssue {
  check_id: string;
  column: string;
  dimension: string;
  examples: Array<Record<string, unknown>>;
  failed_count: number;
  frequency: number;
  message: string;
  metadata: Record<string, unknown>;
  scope: string;
  severity: string;
  table: string;
  total_count: number;
  weight: number;
}

export interface FullAssessmentScore {
  confidence: number;
  dimension_scores: Record<string, {
    confidence: number;
    issue_count: number;
    score: number;
  }>;
  grade: string;
  overall: number;
  status: string;
}

export interface FullAssessmentColumnProfile {
  column_name: string;
  confidence: number;
  dominant_ratio: number;
  evidence: Record<string, unknown>;
  general_type: string;
  is_candidate_key: boolean;
  is_nullable: boolean;
  missing_count: number;
  physical_dtype: string;
  planned_checks: string[];
  semantic_type: string;
  table_name: string;
  total_rows: number;
  unique_count: number;
  unique_ratio: number;
}

export interface FullAssessmentColumnScoreSummary {
  column_name: string;
  critical_issues: number;
  general_type: string;
  high_issues: number;
  issue_count: number;
  low_issues: number;
  medium_issues: number;
  score: number;
  semantic_type: string;
  table_name: string;
  top_issue: string | null;
}

export interface FullAssessmentSelectedPrimaryKey {
  column_name: string;
  confidence: number;
  general_type: string;
  missing_count: number;
  semantic_type: string;
  status: string;
  table_name: string;
  unique_ratio: number;
}

export interface FullAssessmentMetadata {
  column_count: number;
  column_profiles: FullAssessmentColumnProfile[];
  column_score_summary: FullAssessmentColumnScoreSummary[];
  selected_primary_key: FullAssessmentSelectedPrimaryKey | null;
  [key: string]: unknown;
}

export interface FullAssessmentReport {
  dataset_name: string;
  issues: FullAssessmentIssue[];
  metadata: FullAssessmentMetadata;
  recommended_actions: string[];
  score: FullAssessmentScore;
  top_issues: FullAssessmentIssue[];
}

export interface FullAssessmentStepData {
  confidence: number;
  dimension_scores: FullAssessmentDimensionScore[];
  full_report: FullAssessmentReport;
  grade: string;
  overall_score: number;
  status: string;
}

export type FullAssessmentStepResult = ProfileStepResultBase<
  'full_assesment',
  FullAssessmentStepData
>;

export type ProfileStepResult =
  | ColumnBasicStatsStepResult
  | TableBasicStatsStepResult
  | ColumnAdvancedStatsStepResult
  | TableAdvancedStatsStepResult
  | ChartDataStepResult
  | SummaryDataStepResult
  | TopFactsStepResult
  | MissingAnalysisStepResult
  | FindingsStepResult
  | TableIntentStepResult
  | FullAssessmentStepResult;

export type ProfileStepId = ProfileStepResult['step_id'];

export type ProfileStepResultById<TStepId extends ProfileStepId> = Extract<
  ProfileStepResult,
  { step_id: TStepId }
>;

export function getProfileStepResult<TStepId extends ProfileStepId>(
  stepResults: ProfileStepResult[],
  stepId: TStepId
): ProfileStepResultById<TStepId> | undefined {
  return stepResults.find(
    (step): step is ProfileStepResultById<TStepId> => step.step_id === stepId
  );
}
